import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AsyncData } from '@model/common';
import { PromoBannerApi } from '@model/iceberg/service/promo-banner/promo-banner-api';
import { GetPromoBannerDataState, PromoBannerData } from '@model/promo-banner';
import { HYDRATE } from 'next-redux-wrapper';
import { isEqual } from 'lodash';

export enum PromoBannerActions {
  SLICE_NAME = '@PROMO_BANNER',
  FETCH_PROMO_BANNER = '@PROMO_BANNER/fetchPromoBanner'
}

export const fetchPromoBanner = createAsyncThunk(
  PromoBannerActions.FETCH_PROMO_BANNER,
  async (_, { rejectWithValue }) => {
    try {
      const api = new PromoBannerApi();
      const response: GetPromoBannerDataState = await api.getPromoBanner();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response ? error.response.data : error.message);
    }
  }
);

export const INITIAL_PROMO_BANNER_STATE: AsyncData<Array<PromoBannerData>> = {
  data: [] as any,
  loading: false,
  error: null
};

const promoBannerSlice = createSlice({
  name: PromoBannerActions.SLICE_NAME,
  initialState: INITIAL_PROMO_BANNER_STATE,
  reducers: {
    setPromoBanner: (state, action: PayloadAction<PromoBannerData[]>) => {
      state.data = action.payload;
      state.error = null;
      state.loading = false;
    },
    clearPromoBanner: (state) => {
      state.data = [];
      state.error = null;
      state.loading = false;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPromoBanner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPromoBanner.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchPromoBanner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'An unknown error occurred';
      })
      .addCase(HYDRATE, (state, action: any) => {
        if (isEqual(state, INITIAL_PROMO_BANNER_STATE)) {
          const payloadData = action.payload?.promoBanner;
          if (payloadData?.data?.length > 0) {
            return payloadData;
          }
        }
        return state;
      });
  }
});

export const { setPromoBanner, clearPromoBanner, clearError } = promoBannerSlice.actions;
export const promoBannerReducer = promoBannerSlice.reducer;
